<template>
  <div class="upload-modal__content-wrapper">
    <div
      class="upload-modal__image-wrapper"
      :class="{ 'upload-modal__image-wrapper--no-padding': cropper }"
    >
      <template v-if="isImg">
        <div v-if="isShowCropped" class="upload-modal__preview-wrapper">
          <img :src="croppedImageSrc" alt="image" class="upload-modal__preview-image">
        </div>
        <img
          ref="image"
          :src="imageSrc"
          alt="image"
          crossorigin="anonymous"
        >
        <template v-if="isRotate">
          <v-btn
            icon
            class="upload-modal__rotate-button upload-modal__rotate-button--left"
            @click="rotate(true)"
          >
            <v-icon>mdi-rotate-left</v-icon>
          </v-btn>
          <v-btn
            icon
            class="upload-modal__rotate-button upload-modal__rotate-button--right"
            @click="rotate()"
          >
            <v-icon>mdi-rotate-right</v-icon>
          </v-btn>
        </template>
      </template>
      <div v-else class="upload-modal__files-wrapper">
        <h4 class="mb-2">Files:</h4>
        <v-list width="100%" rounded class="pa-0">
          <v-list-item v-for="(file, index) in uploadedFiles" :key="index">
            <v-list-item-icon>
              <v-icon large>
                mdi-file
              </v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title style="max-width: 490px" v-text="file.name" />
              <v-list-item-subtitle v-text="formatBytes(file.size)" />
            </v-list-item-content>
            <v-list-item-action>
              <v-btn icon :disabled="isLoading" @click="$emit('removeFile', index)">
                <v-icon color="grey lighten-1">mdi-close</v-icon>
              </v-btn>
            </v-list-item-action>
          </v-list-item>
        </v-list>
      </div>
      <div v-if="isLoading" class="upload-modal__progress-overlay">
        <div class="upload-modal__progress-box">
          <div class="mb-2">{{ uploadStatus }}</div>
          <v-progress-linear
            :value="uploadProgress"
            :indeterminate="uploadProgress === null || isServerProcessing"
            height="6"
            rounded
          />
          <v-btn
            v-if="uploadProgress !== null && !isServerProcessing"
            outlined
            class="mt-4"
            @click="cancelUpload"
          >
            Cancel
          </v-btn>
        </div>
      </div>
    </div>
    <v-card-actions class="upload-modal__actions">
      <v-btn
        v-if="isImg && cropper"
        :disabled="isLoading"
        outlined
        @click="checkCropped"
      >
        {{ isShowCropped ? 'Hide' : 'Show' }} Preview
      </v-btn>
      <v-spacer />
      <v-btn color="primary" :loading="isLoading" @click="submit">
        <v-icon small>mdi-upload</v-icon> Upload
      </v-btn>
    </v-card-actions>
  </div>
</template>

<script lang="ts">
import Vue, { PropType } from 'vue';
import Cropper from 'cropperjs';

import { CropperFuncNamesEnum, formatBytes } from '@/bundles/Upload/helpers';
import { Nullable } from '@/utils/types';
import { IUploadOptions } from '@/bundles/Upload/interfaces';
import { uploadService } from '@/bundles/Upload/factory/UploadServiceFactory';

interface IUploadCropper {
  cropper: Nullable<Cropper>;
  isRotate: boolean;
  isShowCropped: boolean;
  isLoading: boolean;
  croppedImageSrc: string;
  uploadProgress: Nullable<number>;
  uploadController: Nullable<AbortController>;
}

const FILL_COLOR = '#FFFFFF';

export default Vue.extend({
  name: 'UploadCropper',

  props: {
    imageSrc: {
      type: String,
      default: null
    },
    uploadedFiles: {
      type: Array as PropType<File[]>,
      default: () => []
    },
    activeFunc: {
      type: String as PropType<CropperFuncNamesEnum>,
      default: null
    },
    options: {
      type: Object as PropType<IUploadOptions>,
      required: true,
    },
    isImg: {
      type: Boolean,
      required: true,
    },
    sourceType: {
      type: String,
      default: null,
    },
    sourceId: {
      type: [String, Number],
      default: null,
    },
    isMultiplySizesUpload: Boolean,
    attachmentType: {
      type: String,
      default: null,
    },
    isReturnFileWithoutUpload: Boolean,
  },

  data: (): IUploadCropper => ({
    cropper: null,
    isRotate: false,
    isShowCropped: false,
    isLoading: false,
    croppedImageSrc: '',
    uploadProgress: null,
    uploadController: null,
  }),

  computed: {
    isServerProcessing (): boolean {
      return this.uploadProgress === 100;
    },
    uploadStatus (): string {
      if (this.uploadProgress === null) {
        return 'Preparing image...';
      }

      return this.isServerProcessing ? 'Processing on server...' : `Uploading ${this.uploadProgress}%`;
    },
  },

  watch: {
    isLoading (value: boolean) {
      this.$emit('update:uploading', value);
    },
    activeFunc: {
      handler (newName: Nullable<CropperFuncNamesEnum>, prevName: undefined | CropperFuncNamesEnum) {
        if (newName) {
          this.toggleCropperFunc(newName);
        } else if (prevName && !newName) {
          this.cropper?.destroy();
          this.cropper = null;
          this.isRotate = false;
        }
      },
      immediate: true,
    }
  },

  mounted () {
    this.$eventBus.$on('upload:zoom', this.zoomChangeHandler);
  },

  beforeDestroy () {
    this.cropper?.destroy();
    this.$eventBus.$off('upload:zoom', this.zoomChangeHandler);
  },

  methods: {
    initCropper () {
      if (this.cropper) {
        return;
      }

      this.$nextTick(() => {
        this.cropper = new Cropper((this.$refs.image as HTMLImageElement), {
          background: false,
          viewMode: 0,
          autoCropArea: 1,
          checkCrossOrigin: false,
          ...this.options
        });
      });
    },
    submit () {
      if (!this.uploadedFiles.length) {
        return;
      }

      this.isLoading = true;

      if (this.cropper) {
        const fileType = this.uploadedFiles[0].type || 'image/png';

        const imageData = this.cropper.getData();

        this.cropper.getCroppedCanvas({
          width: Math.round(imageData.width),
          height: Math.round(imageData.height),
        }).toBlob((blob) => {
          if (!blob) {
            return;
          }

          const file = new File(
            [blob],
            this.uploadedFiles[0].name || 'cropped.png', // cropper load only when one file present
            { type: fileType } // file can be null if we upload image from url
          );

          if (this.isReturnFileWithoutUpload) {
            this.$emit('fileUploaded', { originalFiles: [], files: [], file });
            this.isLoading = false;
            return;
          }

          this.saveFile({ files: [file] });
        }, fileType, 0.95);
        return;
      }

      if (this.uploadedFiles.length) {
        this.saveFile({ files: this.uploadedFiles });
      }
    },
    async saveFile ({ files }: { files: File[] }) {
      try {
        this.isLoading = true;
        this.uploadProgress = 0;
        this.uploadController = new AbortController();

        const params: Array<{ name: string; value: any }> = [];

        if (this.attachmentType) {
          params.push(
            { name: 'is_attachment', value: true },
            { name: 'attachment_type', value: this.attachmentType }
          );
        }

        // bind is used to save context of this in uploadService
        const uploadFunction = this.isMultiplySizesUpload ? uploadService.uploadMultiplySizes.bind(uploadService) : uploadService.upload.bind(uploadService);
        const responseFiles = await uploadFunction({
          record: this.sourceType,
          key: this.sourceId,
          files,
          params,
          onUploadProgress: this.onUploadProgress,
          controller: this.uploadController,
        });

        this.$emit('fileUploaded', { originalFiles: files, files: responseFiles });
      } catch (error) {
        if (this.uploadController?.signal.aborted) {
          return;
        }

        const notification = { ...error, status: 0 }; // to show error notification
        this.addNotification(notification);
      } finally {
        this.isLoading = false;
        this.uploadProgress = null;
        this.uploadController = null;
      }
    },
    cancelUpload () {
      this.uploadController?.abort();
    },
    onUploadProgress ({ loaded, total }: ProgressEvent) {
      if (total) {
        this.uploadProgress = Math.round((loaded / total) * 100);
      }
    },
    rotate (isLeft = false) {
      this.cropper?.rotate(isLeft ? -90 : 90);
    },
    toggleCropperFunc (name: CropperFuncNamesEnum) {
      if (!this.isImg) {
        return;
      }

      if (!this.cropper) {
        this.initCropper();
      }

      this.isRotate = name === CropperFuncNamesEnum.rotate;
    },
    hideCroppedImage () {
      this.croppedImageSrc = '';
      this.isShowCropped = false;
    },
    checkCropped () {
      if (this.croppedImageSrc) {
        this.hideCroppedImage();
        return;
      }

      if (!this.cropper) {
        return;
      }

      const imageData = this.cropper.getData();

      this.cropper.getCroppedCanvas({
        width: Math.round(imageData.width),
        height: Math.round(imageData.height),
        fillColor: FILL_COLOR
      }).toBlob((blob) => {
        this.croppedImageSrc = URL.createObjectURL(blob);
        this.isShowCropped = true;
      });
    },
    formatBytes (bytes: number): string {
      return formatBytes(bytes);
    },
    zoomChangeHandler (isZoomIn = false) {
      if (!this.cropper) {
        return;
      }

      this.cropper.zoom(isZoomIn ? 0.1 : -0.1);
    }
  }
});
</script>

<style lang="scss">
@import "~cropperjs/dist/cropper.min.css";

.upload-modal__content-wrapper {
  width: 100%;
  height: 100%;

  .upload-modal__image-wrapper {
    position: relative;
    width: 100%;
    height: 100%;
    padding: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    max-height: calc(100% - 44px); // 44px - height of buttons section
    transition: padding ease 0.3s;
  }

  .upload-modal__image-wrapper--no-padding {
    padding: 0;
  }

  img {
    display: block;
    max-width: 100%;
    max-height: 100%;
  }
}

.upload-modal__progress-overlay {
  position: absolute;
  inset: 0;
  z-index: 3;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(255, 255, 255, 0.85);
}

.upload-modal__progress-box {
  width: 60%;
  text-align: center;
}

.upload-modal__rotate-button {
  position: absolute;
  top: 50%;
  background-color: #ffffff;
  opacity: 0.75;
  transform: translateY(-50%);
}

.upload-modal__rotate-button--left {
  left: 20px;
}

.upload-modal__rotate-button--right {
  right: 20px;
}

.upload-modal__actions {
  padding: 4px !important;
  border-top: 1px solid rgba(0, 0, 0, 0.12);
}

.upload-modal__files-wrapper {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  padding: 8px;
  overflow-y: auto;
  max-height: 100%;
}

.upload-modal__preview-wrapper {
  position: absolute;
  top: 0;
  left: 0;
  background-color: rgb(238, 238, 238);
  z-index: 10;
  width: 100%;
  height: 100%;
  padding: 8px;
  transition: all ease 0.3s;

  .upload-modal__preview-image {
    border: 1px solid rgb(238, 238, 238);
    border-radius: 4px;
    margin: auto;
  }
}
</style>
